import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";

import { Mascot } from "./Mascot";
import { useActiveZone } from "./useActiveZone";
import { useMascotReady } from "./useMascotReady";
import { GENERIC_LINES } from "./zones";
import "./styles.scss";

const DISMISS_KEY = "provenduz.mascot.buddy.hidden";

interface MascotBuddyProps {
  variant?: "isp" | "mvno";
}

const pick = (arr: string[], seed: number) => arr[seed % arr.length];

/**
 * Persistent floating mascot, bottom-right. Waves in after the intro, bobs
 * gently, tilts in 3D toward the cursor, and pops a short speech bubble that
 * reacts to the section in view (or on click / hover). Dismissible.
 */
export const MascotBuddy = ({ variant = "isp" }: MascotBuddyProps) => {
  const reduce = useReducedMotion();
  const ready = useMascotReady();
  const zone = useActiveZone();
  const [hidden, setHidden] = useState(true);
  const [saying, setSaying] = useState<string | null>(null);
  const tick = useRef(0);
  const hideTimer = useRef<number | undefined>(undefined);

  // 3D tilt toward the cursor — real CSS 3D transforms (perspective + rotateX/Y),
  // not a modelled mesh, but it genuinely reacts in three dimensions.
  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const springX = useSpring(rotX, { stiffness: 220, damping: 16, mass: 0.4 });
  const springY = useSpring(rotY, { stiffness: 220, damping: 16, mass: 0.4 });

  const onTilt = (e: MouseEvent<HTMLButtonElement>) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rotY.set(px * 32);
    rotX.set(py * -32);
  };
  const resetTilt = () => {
    rotX.set(0);
    rotY.set(0);
  };

  // mount after a beat so it doesn't fight the intro
  useEffect(() => {
    if (ready !== true) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      dismissed = false;
    }
    if (dismissed) return;
    const t = window.setTimeout(() => setHidden(false), 2600);
    return () => window.clearTimeout(t);
  }, [ready]);

  const say = (text: string, ms = 4200) => {
    window.clearTimeout(hideTimer.current);
    setSaying(text);
    hideTimer.current = window.setTimeout(() => setSaying(null), ms);
  };

  // react to the section in view
  useEffect(() => {
    if (hidden) return;
    tick.current += 1;
    const lines = zone?.lines ?? GENERIC_LINES;
    say(pick(lines, tick.current));
    return () => window.clearTimeout(hideTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone, hidden]);

  const pose = zone?.pose ?? "idle";

  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const onPoke = () => {
    const lines = zone?.lines ?? GENERIC_LINES;
    say(pick(lines, ++tick.current));
  };

  const entrance = useMemo(
    () =>
      reduce
        ? { initial: { opacity: 0 }, animate: { opacity: 1 } }
        : {
            initial: { opacity: 0, y: 80, scale: 0.7 },
            animate: { opacity: 1, y: 0, scale: 1 },
            exit: { opacity: 0, y: 80, scale: 0.7 },
          },
    [reduce]
  );

  return (
    <AnimatePresence>
      {!hidden && (
        <motion.div
          className={`mascot-buddy mascot-buddy--${variant}`}
          {...entrance}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
        >
          <AnimatePresence>
            {saying && (
              <motion.div
                key={saying}
                className="mascot-buddy__bubble"
                initial={{ opacity: 0, y: 8, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.9 }}
                transition={{ duration: 0.22 }}
              >
                {saying}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            className="mascot-buddy__hit"
            aria-label="Falar com o mascote"
            onMouseEnter={onPoke}
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
            onClick={onPoke}
            style={{ perspective: 500 }}
          >
            <motion.div
              style={{ rotateX: springX, rotateY: springY, transformStyle: "preserve-3d" }}
            >
              <Mascot pose={pose} size={116} bob={!reduce} />
            </motion.div>
          </button>

          <button
            className="mascot-buddy__close"
            aria-label="Dispensar mascote"
            onClick={dismiss}
          >
            <i className="bx bx-x" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
